/**
 * The button above the Approval column that accepts every value whose sources all give one value.
 *
 * Expects the button's label, the question it asks, how many values it would accept, busy while an
 * earlier write is in flight, and onConfirm.
 *
 * Renders one green outlined button, which the question, a "Yes, accept" button and a "Cancel"
 * button replace in place once it is pressed.
 *
 * It asks first because one click would otherwise accept many values. This component writes
 * nothing itself: the page counts the values, passes the count, and does the writing in onConfirm.
 */

import { useState } from 'react';
import { CANCEL, YES_ACCEPT } from '@/pages/TaxProfile/wording';

export interface BulkReviewButtonProps {
  /** What the button says while it is not asking. */
  label: string;
  /** What the button asks before it sends anything, naming the count. */
  question: string;
  /** How many values the action would accept. A count of 0 leaves the button disabled. */
  count: number;
  /** True while an earlier write is still in flight. */
  busy?: boolean;
  onConfirm: () => void;
}

export default function BulkReviewButton({
  label,
  question,
  count,
  busy = false,
  onConfirm,
}: BulkReviewButtonProps) {
  const [isAsking, setIsAsking] = useState(false);

  if (isAsking) {
    return (
      <span className='flex flex-wrap items-center justify-end gap-2 text-sm'>
        {question}
        <button
          type='button'
          className='btn btn-sm btn-accent'
          disabled={busy}
          onClick={() => {
            setIsAsking(false);
            onConfirm();
          }}
        >
          {YES_ACCEPT}
        </button>
        <button
          type='button'
          className='btn btn-sm btn-ghost'
          onClick={() => setIsAsking(false)}
        >
          {CANCEL}
        </button>
      </span>
    );
  }

  return (
    <button
      type='button'
      className='btn btn-sm btn-outline btn-accent h-auto max-w-full whitespace-normal py-1'
      disabled={busy || count === 0}
      onClick={() => setIsAsking(true)}
    >
      {label}
    </button>
  );
}
